import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';

import { cn } from '@/lib/utils';

import { toPhoneNumberFormat } from '@/helpers';

type FooterProps = {
  footer_logo_url?: string;
  company_base_settings: {
    email: string | null;
    name: string | null;
    phone: string | null;
    work_time: string | null;
    adress: string | null;
  };
};

type ContactItemProps = {
  icon: ReactNode;
  children: ReactNode;
  href?: string;
  className?: string;
};

const ContactItem = (props: ContactItemProps) => {
  const { icon, children, href, className } = props;

  const content = (
    <span
      className={cn(
        'group flex items-center gap-2 text-xs transition-all duration-300',
        href && 'cursor-pointer',
        className,
      )}
    >
      <span
        className={cn(
          'text-muted-foreground transition-colors duration-300',
          href && 'group-hover:text-primary',
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          'text-muted-foreground transition-colors duration-300',
          href && 'group-hover:text-foreground',
        )}
      >
        {children}
      </span>
    </span>
  );

  if (href) {
    return (
      <a
        href={href}
        className="block"
      >
        {content}
      </a>
    );
  }

  return content;
};

export const Footer = (props: FooterProps) => {
  const { footer_logo_url, company_base_settings } = props;
  const { t } = useTranslation();

  const currentYear = new Date().getFullYear();
  const { phone, email, work_time, adress } = company_base_settings;

  const hasContactInfo = phone || email || work_time || adress;

  return (
    <footer className="bg-muted/50 border-t">
      <div className="px-4 py-6 lg:px-8">
        {/* Main Content */}
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          {/* Brand Section */}
          {footer_logo_url && (
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="bg-foreground flex shrink-0 items-center justify-center rounded-xl px-4 py-2 transition-transform duration-300 hover:scale-105"
              >
                <img
                  src={footer_logo_url}
                  alt="Logo"
                  className="h-6 w-auto brightness-0 invert"
                />
              </a>
              <p className="text-muted-foreground text-xs">
                {t('footerTagline')}
              </p>
            </div>
          )}

          {/* Contact Info */}
          {hasContactInfo && (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {phone && (
                <ContactItem
                  icon={<Phone className="size-3.5" />}
                  href={`tel:${phone}`}
                >
                  {toPhoneNumberFormat(phone)}
                </ContactItem>
              )}
              {email && (
                <ContactItem
                  icon={<Mail className="size-3.5" />}
                  href={`mailto:${email}`}
                >
                  {email}
                </ContactItem>
              )}
              {work_time && (
                <ContactItem icon={<Clock className="size-3.5" />}>
                  {work_time}
                </ContactItem>
              )}
              {adress && (
                <ContactItem icon={<MapPin className="size-3.5" />}>
                  <span
                    className="line-clamp-1"
                    dangerouslySetInnerHTML={{ __html: adress }}
                  />
                </ContactItem>
              )}
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="border-border/50 mt-4 flex flex-col items-center gap-2 border-t pt-4 text-xs sm:flex-row sm:justify-between">
          <p className="text-muted-foreground">
            © 2019–{currentYear} GymSatellite. {t('allRightsReserved')}
          </p>
          <a
            href="https://gymsatellite.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary transition-colors duration-300"
          >
            Powered by <span className="font-semibold">GymSatellite</span>
          </a>
        </div>
      </div>
    </footer>
  );
};
